import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { Box, Typography } from '@mui/material';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
} from '@mui/x-data-grid-premium';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Schedule } from '../scheduler-field.context';
import { useSchedulerContext } from '../scheduler-field.hooks';
import {
  capitalizeDayOfWeek,
  groupAndSortRecordsByDaysOfWeek,
} from '../scheduler-field.utils';
import { daysOfWeekOptionsLong } from '../../days-multiple-select-field/days-multiple-select-field.utils';

const SchedulerDataGrid = () => {
  const { t } = useTranslation();
  const { additionalData, timeFormat, removeSchedule, schedules } =
    useSchedulerContext();

  const groupedAndSortedRecords = groupAndSortRecordsByDaysOfWeek(
    schedules,
    timeFormat,
  );

  const handleRemoveSchedule = useCallback(
    params => () => {
      removeSchedule(params.row);
    },
    [removeSchedule],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'startTime',
        valueFormatter: value =>
          DateTime.fromFormat(value, timeFormat).toFormat('h:mm a'),
        headerName: t('SchedulerDataGrid.column.startTime.label'),
        sortable: false,
        flex: 1,
        display: 'flex',
      },
      {
        field: 'endTime',
        valueFormatter: value =>
          DateTime.fromFormat(value, timeFormat).toFormat('h:mm a'),
        headerName: t('SchedulerDataGrid.column.endTime.label'),
        sortable: false,
        flex: 1,
        display: 'flex',
      },
      {
        field: 'additionalData',
        headerName: additionalData?.additionalDataColumnLabel,
        sortable: false,
        flex: 1,
        display: 'flex',
        renderCell: additionalData?.additionalDataRenderer,
      },
      {
        field: t('SearchDataGrid.column.actions'),
        type: 'actions',
        width: 10,
        getActions: params => [
          <GridActionsCellItem
            icon={<ClearOutlinedIcon />}
            label=""
            onClick={handleRemoveSchedule(params)}
          />,
        ],
      },
    ],
    [
      additionalData?.additionalDataColumnLabel,
      additionalData?.additionalDataRenderer,
      handleRemoveSchedule,
      t,
      timeFormat,
    ],
  );

  return (
    <Box>
      {Object.entries(groupedAndSortedRecords).map(([dayOfWeek, rows]) => (
        <Box key={dayOfWeek} my={2}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontFamily: 'Lato',
              fontStyle: 'normal',
              fontWeight: 700,
              fontSize: '18px',
              lineFeight: '116.7%',
              mb: 1,
            }}
          >
            {
              daysOfWeekOptionsLong().find(option => option.id === dayOfWeek)
                ?.label
            }
          </Typography>
          <DataGridPremium
            rows={rows}
            columns={
              additionalData?.additionalDataColumnLabel
                ? columns
                : columns.filter(item => item.field !== 'additionalData')
            }
            getRowId={(row: Schedule) =>
              `${row.day}${row.startTime}${row.endTime}${
                row.additionalData ? JSON.stringify(row.additionalData) : ''
              }`
            }
            disableColumnMenu
            autoHeight
            disableColumnSelector
            disableDensitySelector
            disableColumnFilter
            disableRowSelectionOnClick
            hideFooter
            density="standard"
            sx={[
              theme => ({
                border: 'none',
                '&.MuiDataGrid-root, .MuiDataGrid-main': {
                  boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)',
                },
                '& .MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-columnHeaders': {
                  border: 'none',
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                },
                '& .MuiDataGrid-footerContainer': {
                  border: 'none',
                },
                '& .MuiDataGrid-cell': {
                  border: 'none',
                },
                '.MuiDataGrid-columnHeader--sorted': {
                  '.MuiDataGrid-sortIcon': {
                    color: theme.palette.primary.main,
                  },
                },
                '.MuiDataGrid-columnHeaderTitle': {
                  fontFamily: 'Lato',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '18px',
                  letterSpacing: '0.17px',
                },
                '.MuiDataGrid-cellContent': {
                  fontFamily: 'Lato',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '143%',
                  letterSpacing: '0.17px',
                },
              }),
            ]}
            data-testid={`${capitalizeDayOfWeek(dayOfWeek)}Scheduler.DataGrid`}
            initialState={{
              pinnedColumns: { right: [t('SearchDataGrid.column.actions')] },
            }}
            slotProps={{
              row: {
                'data-testid': 'Scheduler.DataGrid.Row',
              },
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

export default SchedulerDataGrid;
